import React, {
  memo, useCallback, useState, useRef, useEffect,
  useMemo,
} from 'react';
import {
  func,
} from 'prop-types';
import { useSelector } from 'react-redux';
import Checkbox from '@active/react-ui/Checkbox';
import CheckboxGroup from '@active/react-ui/CheckboxGroup';
import Form, { FormConsumer } from '@active/react-ui/Form';
import FormGroup from '@active/react-ui/FormGroup';
import FormField from '@active/react-ui/FormField';
import FormLabel from '@active/react-ui/FormLabel';
import Locale from '@active/react-utils/i18n/Locale';
import isEmpty from 'lodash/isEmpty';
import WaiverModal from './components/WaiverModal';
import { handleKeyboardEvent } from '../../../shared/utils/commonUtils';
import pubSubInstance from '../../../shared/utils/pubSub.js';

const Waivers = ({
  trackingCallback,
}) => {
  const {
    entities: {
      userInfo: {
        passportInfo: {
          passportBaseUrl,
        } = {},
        waivers = [],
      },
    },
    defaultData,
  } = useSelector(state => state.mainStateData);

  const defaultFormFields = useMemo(() => (isEmpty(defaultData) ? {} : defaultData.waiversData), [ defaultData ]);

  const {
    locale, widgetNameSpace,
  } = useSelector(state => state.propsData);

  const formRef = useRef(null);

  const [ showWaiverModal, setShowWaiverModal ] = useState(false);
  const [ currentIdx, setCurrentIdx ] = useState(null);

  const handleWaiverClick = useCallback((idx, e) => {
    trackingCallback && trackingCallback({ id: 'cui.waiver.clicked' });
    setCurrentIdx(idx);
    setShowWaiverModal(true);
    e.stopPropagation();
    e.preventDefault();
  }, [ trackingCallback, setShowWaiverModal, setCurrentIdx ]);

  const handleClick = useCallback((idx) => {
    return handleWaiverClick.bind(null, idx);
  }, [ handleWaiverClick ]);

  const handleWaiverModalClose = useCallback(() => {
    setCurrentIdx(null);
    setShowWaiverModal(false);
  }, [ showWaiverModal, setShowWaiverModal, currentIdx, setCurrentIdx ]);

  const handleValidate = useCallback(() => {
    return new Promise((resolve) => {
      if (!formRef.current) {
        resolve(true);
        return;
      }
      formRef.current.validateForm({ shouldTouchFields: true }).then((res) => {
        resolve(isEmpty(res));
      });
    });
  }, [ formRef ]);

  const handleSubmit = useCallback(() => {
    return {
      status: true,
      waiversData: {
        ...formRef.current?.values,
      },
    };
  }, [ formRef.current ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-validate-block`, handleValidate);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-validate-block`, handleValidate);
    };
  }, [ handleValidate, widgetNameSpace ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-submit-block`, handleSubmit);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-submit-block`, handleSubmit);
    };
  }, [ handleSubmit, widgetNameSpace ]);

  if (waivers.length === 0) {
    return null;
  }

  return (
    <div className="checkout-ui-waivers cui-mg-t-60">
      <h2 className="second-level-title waivers__title">
        {<Locale id="checkout.lib.components.common.text.waiver"/>}
      </h2>
      <Form
        initialValues={defaultFormFields}
        customizeLayout>
        { waivers.map(({ name }, idx) => name && <FormGroup
          className="checkout-ui-checkbox__group"
          key={idx}
          name={`waiver_${idx}`} required>
          <FormLabel></FormLabel>
          <FormField as={CheckboxGroup} >
            <Checkbox
              value="true"
              tabIndex="0">
              <Locale id="checkout.lib.components.common.text.IAgreeTo" />
            </Checkbox>
            <span
              tabIndex="0"
              role="link"
              onKeyDown={handleKeyboardEvent(handleClick(idx), [ 'Enter' ])} className="link-style checkbox-text" onClick={handleClick(idx)}>
              {name}
            </span>
          </FormField>
        </FormGroup>,
        )}
        <FormConsumer>
          {
            ({
              form,
            }) => {
              formRef.current = form;
            }
          }
        </FormConsumer>
      </Form>
      {
        showWaiverModal
        && <WaiverModal
          passportBaseUrl={passportBaseUrl}
          handleWaiverModalClose={handleWaiverModalClose}
          {...waivers[currentIdx]}
          locale={locale}/>
      }
    </div>
  );
};

Waivers.propTypes = {
  trackingCallback: func,
};

export default memo(Waivers);
